import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { EARNINGS_PURPLE, EarningsCard, EarningsCardSymbol } from './earningsCard';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../stores/quoteTypeStore';
import { useBaseStore } from '@roc/feature-app-core';
import { isNil, LoanSubType } from '@roc/feature-utils';
import { formatCurrency } from '@roc/ui/utils';

interface EarningsCardsProps {
  store: QuoteTypeStore;
}

const useStyles = makeStyles(theme => ({
  // single compact strip: title, then At Closing + Future Estimate = Potential Total
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 3),
    padding: theme.spacing(1.5, 3),
    backgroundColor: '#F6EEFF',
    borderRadius: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: EARNINGS_PURPLE,
    marginRight: theme.spacing(1),
  },
}));

const FACOEarningLoanType = observer(({ store }: EarningsCardsProps) => {
  const { loanEconomicsStore, pricerSummaryStore } = store;
  const outputs = loanEconomicsStore.outputs;
  const loanSubType = store.loanSubtype;
  const rateAvailable = !isNil(pricerSummaryStore?.submittedQuote?.rate);
  const isBroker = pricerSummaryStore?.isBroker;

  const potentialTotalEarningValue = rateAvailable
  ? (
      isBroker
        ? pricerSummaryStore?.submittedQuote?.youMakeForBroker
        : pricerSummaryStore?.isInternalLender
          ? pricerSummaryStore?.submittedQuote?.commissions?.commissionAmount
          : pricerSummaryStore?.submittedQuote?.youMakeForWholesale
    ) ?? 0
  : 0;

  const potentialTotalPercentValue = rateAvailable
  ? (
      isBroker
        ? pricerSummaryStore?.submittedQuote?.youMakeForBrokerPercent
        : pricerSummaryStore?.isInternalLender
          ? pricerSummaryStore?.submittedQuote?.commissions?.commissionPercentage
          : pricerSummaryStore?.submittedQuote?.youMakeForWholesalePercent
    ) ?? 0
  : 0;

  switch (loanSubType) {
    case LoanSubType.SINGLE_PROPERTY:
      return (
        <>
          <EarningsCard
            title={`Potential Total`}
            earnings={potentialTotalEarningValue}
            percentage={potentialTotalPercentValue}
            percentageDescription='Yield Spread'
            position="right"
          />
        </>
      )
    case LoanSubType.RENTAL_PORTFOLIO:
      return (
        <>
          <EarningsCard
            title={`Potential Total`}
            earnings={potentialTotalEarningValue}
            percentage={potentialTotalPercentValue}
            percentageDescription='Yield Spread'
            position="right"
          />
        </>
      )
    default:
      return (
        <>
          <EarningsCard
            title={`At Closing`}
            earnings={outputs?.lenderMakesNow}
            percentage={outputs?.totalLenderPoints}
            percentageDescription="of the Total Loan"
            position="left"
            tooltip="The combination of origination points and fees paid out when the loan closes."
          />
          <EarningsCardSymbol text='+' />
          <EarningsCard
            title="Future Estimate"
            earnings={outputs?.lenderFutureEarnings}
            percentage={outputs?.lenderYieldSpread}
            percentageDescription="Yield Spread"
            position="center"
            tooltip="This amount is an estimate. Your earnings will vary monthly based on how much of the construction holdback your borrower draws."
          />
          <EarningsCardSymbol text='=' />
          <EarningsCard
            title={`Potential Total`}
            earnings={outputs?.lenderTotalEarnings}
            position="right"
          />
        </>
      )
  }
})

export const EarningsCards = observer(({ store }: EarningsCardsProps) => {
  const classes = useStyles();
  const { globalStore } = useBaseStore();
  const lenderTradeName = globalStore.lenderInfo?.lenderTradeName;

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>
        {lenderTradeName} Earnings
      </Typography>
      <FACOEarningLoanType store={store} />
    </Box>
  );
});
